import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Package,
  ChevronDown,
  ChevronUp,
  Users,
  Store,
  TrendingUp,
  Truck,
  PackageCheck,
  CheckCircle,
  XCircle,
  RotateCcw,
  CreditCard,
  ShoppingBag,
  MessageSquare,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Palette,
  BookOpen,
  PieChart,
  Clock,
  BarChart3,
  Target,
  Boxes,
  MapPin,
  Landmark,
  Briefcase,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
// ✅ Sidebar ka apna alag permission hook tha jo context se out-of-sync
//    ho jaata tha — ab wahi single source use karta hai.
import { usePermissions } from "../context/PermissionContext";
import { MODULES, isModuleEnabled, isRouteEnabled } from "../config/modules";
import vendorIcon from "../assets/vendor.svg";
import sidebarLog from "../assets/saidbar-log.svg";

const P = {
  LEAD_VIEW: "LEAD_VIEW",
  LEAD_TO_ORDER_VIEW: "LEAD_TO_ORDER_VIEW",
  ORDERS_VIEW: "ORDERS_VIEW",
  ORDERS_CREATE: "ORDERS_CREATE",
  VERIFIED_VIEW: "VERIFIED_VIEW",
  DISPATCH_VIEW: "DISPATCH_VIEW",
  IN_TRANSIT_VIEW: "IN_TRANSIT_VIEW",
  OFD_VIEW: "OFD_VIEW",
  DELIVERED_VIEW: "DELIVERED_VIEW",
  NDR_VIEW: "NDR_VIEW",
  RTO_VIEW: "RTO_VIEW",
  BILLING_VIEW: "BILLING_VIEW",
  ORDER_DATA_VIEW: "ORDER_DATA_VIEW",
  LEAD_DATA_VIEW: "LEAD_DATA_VIEW",
  PRODUCTS_VIEW: "PRODUCTS_VIEW",
  EMPLOYEES_VIEW: "EMPLOYEES_VIEW",
  B2B_VIEW: "B2B_VIEW",
  LEDGER_VIEW: "LEDGER_VIEW",
  CHAT_HISTORY_VIEW: "CHAT_HISTORY_VIEW",
  // ✅ New permissions
  ANALYTICS_VIEW: "ANALYTICS_VIEW",
  ANALYTICS_DOWNLOAD: "ANALYTICS_DOWNLOAD",
  ATTENDANCE_VIEW: "ATTENDANCE_VIEW",
  ATTENDANCE_EDIT: "ATTENDANCE_EDIT",
  MIS_REPORTS_VIEW: "MIS_REPORTS_VIEW",
  DASHBOARD_VIEW: "DASHBOARD_VIEW",
  VERIFIED_ASSIGN: "VERIFIED_ASSIGN",
  TARGET_VIEW: "TARGET_VIEW", // ✅ ADD
  TARGET_ASSIGN: "TARGET_ASSIGN",
  ORDER_REASSIGN_VIEW: "ORDER_REASSIGN_VIEW",
  ORDER_REASSIGN_UPDATE: "ORDER_REASSIGN_UPDATE",
  // 🏦 Fintech
  FINTECH_DASHBOARD_VIEW: "FINTECH_DASHBOARD_VIEW",
  LOAN_VIEW: "LOAN_VIEW",
  LOAN_CREATE: "LOAN_CREATE",
  COLLECTION_VIEW: "COLLECTION_VIEW",
  LOAN_ACCOUNT_VIEW: "LOAN_ACCOUNT_VIEW",
  FINTECH_REPORT_VIEW: "FINTECH_REPORT_VIEW",
  DSA_DASHBOARD_VIEW: "DSA_DASHBOARD_VIEW",
  DSA_LENDER_VIEW: "DSA_LENDER_VIEW",
  DSA_PIPELINE_VIEW: "DSA_PIPELINE_VIEW",
  DSA_COMMISSION_VIEW: "DSA_COMMISSION_VIEW",
};

// Client deployment ke modules (config/modules.js) — build time pe fix
const LOGISTICS_ON = isModuleEnabled(MODULES.logistics);
const PHARMA_ON = isModuleEnabled(MODULES.pharma);
const FINTECH_LENDING_ON = isModuleEnabled(MODULES.fintech_lending);
const FINTECH_DSA_ON = isModuleEnabled(MODULES.fintech_dsa);

function Sidebar() {
  const { currentTheme, changeTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // `has` already ADMIN bypass karta hai (PermissionContext me)
  const { has, isAdmin } = usePermissions();
  const canSee = (code) => has(code);

  const [openDropdowns, setOpenDropdowns] = useState({
    dashboard: location.pathname.startsWith("/dashboard"),
    analytics: location.pathname.startsWith("/analytics"),
    orders: location.pathname.startsWith("/orders"),
    dispatch:
      location.pathname.startsWith("/Dispatch") ||
      location.pathname.startsWith("/dispatch"),
    ndr:
      location.pathname.startsWith("/ndr") ||
      location.pathname.startsWith("/NDR"),
    inTransit: location.pathname.startsWith("/in-transit"),
    ofd: location.pathname.startsWith("/OFD"),
    delivered: location.pathname.startsWith("/Delivered"),
    rto: location.pathname.startsWith("/rto"),
    employees:
      location.pathname.startsWith("/floor") ||
      location.pathname.startsWith("/employees"),
    b2bPartners:
      location.pathname.startsWith("/b2b-partners") ||
      location.pathname.startsWith("/purchase"),
    ledger: location.pathname.startsWith("/ledger"),
    billing: location.pathname.startsWith("/billing"),
    products: location.pathname.startsWith("/products"),
    chatHistory: location.pathname.startsWith("/chat-history"),
    settings: location.pathname.startsWith("/settings"),
    verified: location.pathname.startsWith("/verified"),
    leads: location.pathname.startsWith("/leads"),
    attendance: location.pathname.startsWith("/attendance"),
    misReports: location.pathname.startsWith("/mis-reports"),
    target: location.pathname.startsWith("/target"),
    stockManagement: location.pathname.startsWith("/stock-management"),
    fintech: location.pathname.startsWith("/fintech"),
    theme: false,
  });

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(
    () => parseInt(localStorage.getItem("sidebarWidth")) || 360,
  );
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || isCollapsed) return;
      const newWidth = e.clientX;
      if (newWidth >= 180 && newWidth <= 500) setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        localStorage.setItem("sidebarWidth", sidebarWidth);
      }
    };
    if (isResizing) {
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, isCollapsed, sidebarWidth]);

  useEffect(() => {
    localStorage.setItem("lastPath", location.pathname);
    if (
      location.pathname.startsWith("/b2b-partners") ||
      location.pathname.startsWith("/purchase")
    )
      setOpenDropdowns((prev) => ({ ...prev, b2bPartners: true }));
    else if (location.pathname.startsWith("/ledger"))
      setOpenDropdowns((prev) => ({ ...prev, ledger: true }));
    else if (location.pathname.startsWith("/stock-management"))
      setOpenDropdowns((prev) => ({ ...prev, stockManagement: true }));
  }, [location.pathname]);

  const toggleDropdown = (menu) =>
    setOpenDropdowns((prev) => ({ ...prev, [menu]: !prev[menu] }));
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path);
  const savePathAndNavigate = (path) => {
    localStorage.setItem("lastPath", path);
    navigate(path);
  };

  const menuBtn = (active, isOpen) => `
    w-full flex items-center ${isCollapsed ? "justify-center px-4 py-4" : "gap-[12px] px-4 py-3"}
    transition-all whitespace-nowrap font-bold relative group
    ${active ? (isOpen ? "rounded-t-xl" : "rounded-xl shadow-md") : "hover:bg-black/5 rounded-xl"}
  `;
  const iconSize = isCollapsed ? "w-6 h-6" : "w-5 h-5";

  const SubMenu = ({ items }) => (
    <div className="rounded-b-lg pb-2 shadow-lg bg-white/10 backdrop-blur-md">
      {items.filter(({ path }) => isRouteEnabled(path)).map(({ label, path }) => (
        <button
          key={path}
          onClick={() => savePathAndNavigate(path)}
          className={`w-full text-left pl-12 pr-4 py-2 font-bold hover:bg-white/10 transition-all flex items-center ${isActive(path) ? "bg-white/20" : ""}`}
          style={{ color: currentTheme.textColor }}
        >
          <span className="mr-[10px] text-xs">•</span>
          <span className="whitespace-nowrap">{label}</span>
        </button>
      ))}
    </div>
  );

  const Dropdown = ({ id, children, items }) => (
    <div>
      {children}
      {!isCollapsed && (
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${openDropdowns[id] ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}
        >
          <SubMenu items={items} />
        </div>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      {/* Placeholder to reserve sidebar space in flex layout */}
      <div
        className={isResizing ? "" : "transition-all duration-300"}
        style={{
          width: isCollapsed ? 80 : sidebarWidth,
          minWidth: isCollapsed ? 80 : sidebarWidth,
          flexShrink: 0,
        }}
      />
      <aside
        className={`hide-scrollbar fixed top-[74px] bottom-0 flex-shrink-0 z-40 shadow-2xl flex flex-col ${isResizing ? "" : "transition-all duration-300"}`}
        style={{
          width: isCollapsed ? 80 : sidebarWidth,
          minWidth: isCollapsed ? 80 : sidebarWidth,
          background: currentTheme.cssGradient,
          color: currentTheme.textColor,
        }}
      >
        {!isCollapsed && (
          <div
            className="fixed top-[74px] bottom-0 w-3 cursor-col-resize z-50 hover:bg-black/10 transition-colors"
            style={{
              left: `${sidebarWidth - 3}px`,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              if (!isCollapsed) setIsResizing(true);
            }}
          />
        )}
        <div className="p-4 border-b border-white/10">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full flex items-center justify-center p-3 rounded-xl transition-all font-bold hover:shadow-lg bg-white/20 hover:bg-white/30"
            style={{ color: currentTheme.textColor }}
          >
            {isCollapsed ? (
              <PanelLeftOpen className="w-6 h-6" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5 flex-shrink-0" />
                <span className="ml-[10px] font-bold whitespace-nowrap">
                  Collapse Menu
                </span>
              </>
            )}
          </button>
        </div>

        <nav
          className="p-4 space-y-2 flex-1 overflow-y-auto hide-scrollbar scroll-smooth relative"
          style={{ zIndex: 10 }}
          onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 10)}
        >
          {/* ── Dashboard — sabko dikhega ── */}
          <div>
            <button
              onClick={() => {
                toggleDropdown("dashboard");
                savePathAndNavigate(
                  isAdmin ? "/dashboard/realtime" : "/dashboard",
                );
              }}
              className={menuBtn(
                openDropdowns.dashboard || isActive("/dashboard"),
                openDropdowns.dashboard,
              )}
              style={{
                background:
                  openDropdowns.dashboard || isActive("/dashboard")
                    ? currentTheme.color
                    : "transparent",
              }}
              title={isCollapsed ? "Dashboard" : ""}
            >
              <div className="flex-shrink-0 flex items-center justify-center">
                <svg
                  className={iconSize}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </div>
              {!isCollapsed && (
                <>
                  <span className="font-bold flex-1 text-left whitespace-nowrap">
                    Dashboard
                  </span>
                  {isAdmin &&
                    (openDropdowns.dashboard ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    ))}
                </>
              )}
            </button>
            {/* ✅ Dashboard submenus — sirf ADMIN + DASHBOARD_VIEW */}
            {!isCollapsed && isAdmin && canSee(P.DASHBOARD_VIEW) && (
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${openDropdowns.dashboard ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}
              >
                <SubMenu
                  items={[
                    { label: "Real-time", path: "/dashboard/realtime" },
                    { label: "Lead", path: "/dashboard/lead" },
                    { label: "Order", path: "/dashboard/order" },
                    { label: "Inventory", path: "/stock-management/dashboard" },
                  ]}
                />
              </div>
            )}
          </div>

          {/* ── 🏦 Fintech & Lending Module — module on + koi ek fintech permission ── */}
          {FINTECH_LENDING_ON &&
            (canSee(P.FINTECH_DASHBOARD_VIEW) ||
              canSee(P.LOAN_VIEW) ||
              canSee(P.LOAN_CREATE) ||
              canSee(P.COLLECTION_VIEW) ||
              canSee(P.LOAN_ACCOUNT_VIEW) ||
              canSee(P.FINTECH_REPORT_VIEW)) && (
          <Dropdown
            id="fintech"
            items={[
              ...(canSee(P.FINTECH_DASHBOARD_VIEW)
                ? [{ label: "Fintech Dashboard", path: "/fintech/dashboard" }]
                : []),
              ...(canSee(P.LOAN_VIEW)
                ? [{ label: "Loan Applications", path: "/fintech/loans" }]
                : []),
              ...(canSee(P.LOAN_CREATE)
                ? [{ label: "New Application", path: "/fintech/loans/new" }]
                : []),
              ...(canSee(P.LOAN_ACCOUNT_VIEW)
                ? [{ label: "Loan Accounts", path: "/fintech/loan-accounts" }]
                : []),
              ...(canSee(P.COLLECTION_VIEW)
                ? [{ label: "Collections & DPD", path: "/fintech/collections" }]
                : []),
              ...(canSee(P.LOAN_VIEW)
                ? [{ label: "Loan Products", path: "/fintech/loan-products" }]
                : []),
              // Menu dikh raha hai matlab koi fintech permission hai — calculator sabke liye
              { label: "Loan Calculator", path: "/fintech/calculator" },
              ...(canSee(P.FINTECH_REPORT_VIEW)
                ? [{ label: "RBI Compliance Report", path: "/fintech/reports/rbi" }]
                : []),
            ]}
          >
            <button
              onClick={() => toggleDropdown("fintech")}
              className={menuBtn(
                openDropdowns.fintech || isActive("/fintech"),
                openDropdowns.fintech,
              )}
              style={{
                background:
                  openDropdowns.fintech || isActive("/fintech")
                    ? currentTheme.color
                    : "transparent",
              }}
              title={isCollapsed ? "Fintech & Loans" : ""}
            >
              <div className="flex-shrink-0">
                <Landmark className={iconSize} />
              </div>
              {!isCollapsed && (
                <>
                  <span className="font-bold flex-1 text-left whitespace-nowrap">
                    Fintech & Loans
                  </span>
                  {openDropdowns.fintech ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                  )}
                </>
              )}
            </button>
          </Dropdown>
          )}

          {/* ── 🤝 DSA / Loan Sourcing — module on + koi ek DSA permission ── */}
          {FINTECH_DSA_ON &&
            (canSee(P.DSA_DASHBOARD_VIEW) ||
              canSee(P.DSA_PIPELINE_VIEW) ||
              canSee(P.DSA_LENDER_VIEW) ||
              canSee(P.DSA_COMMISSION_VIEW)) && (
          <Dropdown
            id="dsa"
            items={[
              ...(canSee(P.DSA_DASHBOARD_VIEW)
                ? [{ label: "DSA Dashboard", path: "/dsa/dashboard" }]
                : []),
              ...(canSee(P.DSA_PIPELINE_VIEW)
                ? [{ label: "Pipeline", path: "/dsa/pipeline" }]
                : []),
              ...(canSee(P.DSA_LENDER_VIEW)
                ? [{ label: "Lender Master", path: "/dsa/lenders" }]
                : []),
              ...(canSee(P.DSA_COMMISSION_VIEW)
                ? [{ label: "Commissions", path: "/dsa/commissions" }]
                : []),
            ]}
          >
            <button
              onClick={() => toggleDropdown("dsa")}
              className={menuBtn(
                openDropdowns.dsa || isActive("/dsa"),
                openDropdowns.dsa,
              )}
              style={{
                background:
                  openDropdowns.dsa || isActive("/dsa")
                    ? currentTheme.color
                    : "transparent",
              }}
              title={isCollapsed ? "DSA / Loan Sourcing" : ""}
            >
              <div className="flex-shrink-0">
                <Briefcase className={iconSize} />
              </div>
              {!isCollapsed && (
                <>
                  <span className="font-bold flex-1 text-left whitespace-nowrap">
                    DSA / Sourcing
                  </span>
                  {openDropdowns.dsa ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                  )}
                </>
              )}
            </button>
          </Dropdown>
          )}

          {/* ── Analytics — ANALYTICS_VIEW permission ── */}
          {canSee(P.ANALYTICS_VIEW) && (
            <Dropdown
              id="analytics"
              items={[
                { label: "Analytics Report", path: "/analytics" },
                { label: "AHT bucket", path: "/analytics/aht-bucket" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("analytics")}
                className={menuBtn(
                  openDropdowns.analytics || isActive("/analytics"),
                  openDropdowns.analytics,
                )}
                style={{
                  background:
                    openDropdowns.analytics || isActive("/analytics")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Analytics" : ""}
              >
                <div className="flex-shrink-0">
                  <PieChart className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Analytics
                    </span>
                    {openDropdowns.analytics ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── MIS Reports — MIS_REPORTS_VIEW permission ── */}
          {canSee(P.MIS_REPORTS_VIEW) && (
            <div>
              <button
                onClick={() => toggleDropdown("misReports")}
                className={menuBtn(
                  openDropdowns.misReports || isActive("/mis-reports"),
                  openDropdowns.misReports,
                )}
                style={{
                  background:
                    openDropdowns.misReports || isActive("/mis-reports")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "MIS Reports" : ""}
              >
                <div className="flex-shrink-0">
                  <BarChart3 className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      MIS Reports
                    </span>
                    {openDropdowns.misReports ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
              {!isCollapsed && (
                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${openDropdowns.misReports ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}
                >
                  <div className="rounded-b-lg pb-2 shadow-lg bg-white/10 backdrop-blur-md">
                    <button
                      onClick={() =>
                        savePathAndNavigate("/mis-reports/business-dashboard")
                      }
                      className={`w-full text-left pl-12 pr-4 py-2 font-bold hover:bg-white/10 transition-all flex items-center ${location.pathname === "/mis-reports/business-dashboard" ? "bg-white/20" : ""}`}
                      style={{ color: currentTheme.textColor }}
                    >
                      <span className="mr-[10px] text-xs">•</span>
                      <span className="whitespace-nowrap">
                        Business Dashboard
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Attendance — sabko dikhega ── */}
          {canSee(P.ATTENDANCE_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/attendance")}
              className={menuBtn(isActive("/attendance"), false)}
              style={{
                background: isActive("/attendance")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "Attendance" : ""}
            >
              <div className="flex-shrink-0">
                <Clock className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  Attendance
                </span>
              )}
            </button>
          )}

          {/* ── Lead — LEAD_VIEW permission ── */}
          {canSee(P.LEAD_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/leads")}
              className={menuBtn(isActive("/leads"), false)}
              style={{
                background: isActive("/leads")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "Lead" : ""}
            >
              <div className="flex-shrink-0 flex items-center justify-center">
                <svg
                  className={iconSize}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  Lead
                </span>
              )}
            </button>
          )}

          {/* ── Orders — ORDERS_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.ORDERS_VIEW) && (
            <Dropdown
              id="orders"
              items={[
                { label: "All Orders", path: "/orders" },
                ...(canSee(P.ORDERS_CREATE)
                  ? [{ label: "Add New Order", path: "/orders/add" }]
                  : []),
                { label: "Draft Order", path: "/orders/draft" },
                ...(canSee(P.ORDER_REASSIGN_VIEW)
                  ? [{ label: "Order Reassign", path: "/orders/reassign" }]
                  : []), // ✅ ADD
              ]}
            >
              <button
                onClick={() => toggleDropdown("orders")}
                className={menuBtn(
                  openDropdowns.orders || isActive("/orders"),
                  openDropdowns.orders,
                )}
                style={{
                  background:
                    openDropdowns.orders || isActive("/orders")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Orders" : ""}
              >
                <div className="flex-shrink-0">
                  <Package className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Orders
                    </span>
                    {openDropdowns.orders ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── Verified — VERIFIED_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.VERIFIED_VIEW) && (
            <Dropdown
              id="verified"
              items={[
                { label: "Verify Order", path: "/verified" },
                ...(canSee(P.VERIFIED_ASSIGN)
                  ? [{ label: "Manual Assign", path: "/ManualAssignPanel" }]
                  : []),
              ]}
            >
              <button
                onClick={() => toggleDropdown("verified")}
                className={menuBtn(
                  openDropdowns.verified || isActive("/verified"),
                  openDropdowns.verified,
                )}
                style={{
                  background:
                    openDropdowns.verified || isActive("/verified")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Verified" : ""}
              >
                <div className="flex-shrink-0 flex items-center justify-center">
                  <svg
                    className={iconSize}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Verify
                    </span>
                    {openDropdowns.verified ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── Dispatch — DISPATCH_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.DISPATCH_VIEW) && (
            <Dropdown
              id="dispatch"
              items={[
                { label: "Verified Order", path: "/DispatchOrderverified" },
                { label: "Dispatch Order", path: "/dispatched" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("dispatch")}
                className={menuBtn(
                  openDropdowns.dispatch ||
                    isActive("/Dispatch") ||
                    isActive("/dispatch"),
                  openDropdowns.dispatch,
                )}
                style={{
                  background:
                    openDropdowns.dispatch ||
                    isActive("/Dispatch") ||
                    isActive("/dispatch")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Dispatch" : ""}
              >
                <div className="flex-shrink-0 flex items-center justify-center">
                  <svg
                    className={iconSize}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Dispatch
                    </span>
                    {openDropdowns.dispatch ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── In-Transit — IN_TRANSIT_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.IN_TRANSIT_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/InTransit")}
              className={menuBtn(isActive("/InTransit"), false)}
              style={{
                background: isActive("/InTransit")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "In-Transit" : ""}
            >
              <div className="flex-shrink-0">
                <Truck className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  In-Transit
                </span>
              )}
            </button>
          )}

          {/* ── OFD — OFD_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.OFD_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/OFDOrders")}
              className={menuBtn(isActive("/OFDOrders"), false)}
              style={{
                background: isActive("/OFDOrders")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "OFD" : ""}
            >
              <div className="flex-shrink-0">
                <PackageCheck className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  OFD
                </span>
              )}
            </button>
          )}

          {/* ── Delivered — DELIVERED_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.DELIVERED_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/DeliveredOrders")}
              className={menuBtn(isActive("/DeliveredOrders"), false)}
              style={{
                background: isActive("/DeliveredOrders")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "Delivered" : ""}
            >
              <div className="flex-shrink-0">
                <CheckCircle className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  Delivered
                </span>
              )}
            </button>
          )}

          {/* ── NDR — NDR_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.NDR_VIEW) && (
            <Dropdown
              id="ndr"
              items={[
                { label: "NDR", path: "/NDROrders" },
                { label: "NDR Request", path: "/NDRRequest" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("ndr")}
                className={menuBtn(
                  openDropdowns.ndr ||
                    isActive("/ndr") ||
                    isActive("/NDROrders"),
                  openDropdowns.ndr,
                )}
                style={{
                  background:
                    openDropdowns.ndr ||
                    isActive("/ndr") ||
                    isActive("/NDROrders")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "NDR" : ""}
              >
                <div className="flex-shrink-0">
                  <XCircle className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      NDR
                    </span>
                    {openDropdowns.ndr ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── RTO — RTO_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.RTO_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/rto")}
              className={menuBtn(isActive("/rto"), false)}
              style={{
                background: isActive("/rto")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "RTO" : ""}
            >
              <div className="flex-shrink-0">
                <RotateCcw className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  RTO
                </span>
              )}
            </button>
          )}

          {/* ── Billing — BILLING_VIEW permission ── */}
          {LOGISTICS_ON && canSee(P.BILLING_VIEW) && (
            <Dropdown
              id="billing"
              items={[
                { label: "Self profile", path: "/billing/self-profile" },
                { label: "Billing list", path: "/billing/list" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("billing")}
                className={menuBtn(
                  openDropdowns.billing || isActive("/billing"),
                  openDropdowns.billing,
                )}
                style={{
                  background:
                    openDropdowns.billing || isActive("/billing")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Billing" : ""}
              >
                <div className="flex-shrink-0">
                  <CreditCard className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Billing
                    </span>
                    {openDropdowns.billing ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── Products — PRODUCTS_VIEW permission ── */}
          {PHARMA_ON && canSee(P.PRODUCTS_VIEW) && (
            <Dropdown
              id="products"
              items={[
                { label: "Product category", path: "/products/category" },
                { label: "Product subcategory", path: "/products/subcategory" },
                { label: "Product list", path: "/products/list" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("products")}
                className={menuBtn(
                  openDropdowns.products || isActive("/products"),
                  openDropdowns.products,
                )}
                style={{
                  background:
                    openDropdowns.products || isActive("/products")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Products" : ""}
              >
                <div className="flex-shrink-0">
                  <ShoppingBag className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Products
                    </span>
                    {openDropdowns.products ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── Employees — EMPLOYEES_VIEW permission ── */}
          {canSee(P.EMPLOYEES_VIEW) && (
            <Dropdown
              id="employees"
              items={[
                { label: "Floor", path: "/floor" },
                {
                  label: "User Permission",
                  path: "/employees/user-permission",
                },
                { label: "Employees", path: "/employees" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("employees")}
                className={menuBtn(
                  openDropdowns.employees ||
                    isActive("/floor") ||
                    isActive("/employees"),
                  openDropdowns.employees,
                )}
                style={{
                  background:
                    openDropdowns.employees ||
                    isActive("/floor") ||
                    isActive("/employees")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Employees" : ""}
              >
                <div className="flex-shrink-0">
                  <Users className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Employees
                    </span>
                    {openDropdowns.employees ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── B2B Partners — B2B_VIEW permission ── */}
          {PHARMA_ON && canSee(P.B2B_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/b2b-partners")}
              className={menuBtn(isActive("/b2b-partners"), false)}
              style={{
                background: isActive("/b2b-partners")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "Vendors" : ""}
            >
              <div className="flex-shrink-0 flex items-center justify-center">
                <img src={vendorIcon} alt="Vendors" className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  Vendors
                </span>
              )}
            </button>
          )}

          {/* ── Centers ── */}
          {/* {PHARMA_ON && canSee(P.B2B_VIEW) && (
            <button
              onClick={() => savePathAndNavigate("/centers")}
              className={menuBtn(isActive("/centers"), false)}
              style={{
                background: isActive("/centers")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "Centers" : ""}
            >
              <div className="flex-shrink-0 flex items-center justify-center">
                <MapPin className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  Centers
                </span>
              )}
            </button>
          )} */}

          {/* ── Ledger — LEDGER_VIEW permission ──
          {canSee(P.LEDGER_VIEW) && (
            <Dropdown id="ledger" items={[
              { label: 'Partners', path: '/ledger/partners' },
              { label: 'Stock', path: '/ledger/stock' },
            ]}>
              <button onClick={() => toggleDropdown('ledger')}
                className={menuBtn(openDropdowns.ledger || isActive('/ledger'), openDropdowns.ledger)}
                style={{ background: openDropdowns.ledger || isActive('/ledger') ? currentTheme.color : 'transparent' }}
                title={isCollapsed ? 'Ledger' : ''}
              >
                <div className="flex-shrink-0"><BookOpen className={iconSize} /></div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">Ledger</span>
                    {openDropdowns.ledger ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
                  </>
                )}
              </button>
            </Dropdown>
          )} */}

          {/* ── Settings — sirf ADMIN ── */}
          {isAdmin && (
            <Dropdown
              id="settings"
              items={[
                { label: "Lead API", path: "/settings/lead-api" },
                { label: "Order Campaign", path: "/settings/order-campaign" },
                {
                  label: "Lead Disposition",
                  path: "/settings/lead-disposition",
                },
                { label: "Courier API", path: "/settings/courier-api" },
                { label: "Velocity Warehouse", path: "/settings/velocity-warehouse" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("settings")}
                className={menuBtn(
                  openDropdowns.settings || isActive("/settings"),
                  openDropdowns.settings,
                )}
                style={{
                  background:
                    openDropdowns.settings || isActive("/settings")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Settings" : ""}
              >
                <div className="flex-shrink-0">
                  <Settings className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Settings
                    </span>
                    {openDropdowns.settings ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── Target — sirf ADMIN ── */}
          {(isAdmin || has(P.TARGET_VIEW) || has(P.TARGET_ASSIGN)) && (
            <button
              onClick={() => savePathAndNavigate("/target")}
              className={menuBtn(isActive("/target"), false)}
              style={{
                background: isActive("/target")
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "Target" : ""}
            >
              <div className="flex-shrink-0">
                <Target className={iconSize} />
              </div>
              {!isCollapsed && (
                <span className="font-bold text-left whitespace-nowrap">
                  Target
                </span>
              )}
            </button>
          )}

          {/* ── Stock Management — sirf ADMIN ── */}
          {PHARMA_ON && isAdmin && (
            <Dropdown
              id="stockManagement"
              items={[
                { label: "Stock In", path: "/stock-management/stock-in" },
                { label: "Transfer", path: "/stock-management/transfer" },
                { label: "Returns", path: "/stock-management/returns" },
                { label: "Ledger", path: "/stock-management/ledger" },
                { label: "Sampling", path: "/stock-management/sampling" },
              ]}
            >
              <button
                onClick={() => toggleDropdown("stockManagement")}
                className={menuBtn(
                  openDropdowns.stockManagement || isActive("/stock-management"),
                  openDropdowns.stockManagement,
                )}
                style={{
                  background:
                    openDropdowns.stockManagement || isActive("/stock-management")
                      ? currentTheme.color
                      : "transparent",
                }}
                title={isCollapsed ? "Stock Management" : ""}
              >
                <div className="flex-shrink-0">
                  <Boxes className={iconSize} />
                </div>
                {!isCollapsed && (
                  <>
                    <span className="font-bold flex-1 text-left whitespace-nowrap">
                      Stock Management
                    </span>
                    {openDropdowns.stockManagement ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}
              </button>
            </Dropdown>
          )}

          {/* ── Theme — sabko dikhega ── */}
          <div>
            <button
              onClick={() => toggleDropdown("theme")}
              className={menuBtn(openDropdowns.theme, openDropdowns.theme)}
              style={{
                background: openDropdowns.theme
                  ? currentTheme.color
                  : "transparent",
              }}
              title={isCollapsed ? "Theme" : ""}
            >
              <Palette className={iconSize} />
              {!isCollapsed && (
                <>
                  <span className="font-bold flex-1">Theme</span>
                  {openDropdowns.theme ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </>
              )}
            </button>
            {!isCollapsed && (
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${openDropdowns.theme ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}
              >
                <div className="rounded-b-lg p-4 shadow-lg bg-white/10 backdrop-blur-md">
                  <div className="grid grid-cols-5 gap-3 justify-items-center">
                    {[
                      //white color theme///
                      { name: "Teal (Default)", color: "#f8f3f3ff" },
                      { name: "Ayurvedic", color: "#557A46" },
                      { name: "Blue", color: "#459B7A" },
                      /// black color theme////
                      { name: "Purple", color: "#3f3e3fff" },
                      { name: "Gray", color: "#6B7280" },
                      // { name: 'Orange', color: '#F97316' },
                      // { name: 'Green', color: '#10B981' },
                      // { name: 'Pink', color: '#EC4899' },
                      // { name: 'Yellow', color: '#EAB308' },
                      // { name: 'Black', color: '#2c3e50' },
                    ].map((theme) => (
                      <button
                        key={theme.name}
                        onClick={() => changeTheme(theme.name)}
                        className="w-8 h-8 rounded-full border-2 border-black hover:scale-110 transition-all shadow-md"
                        style={{ backgroundColor: theme.color }}
                        title={theme.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* ── Sidebar Ayurvedic Brand Logo Watermark ── */}
        {!isCollapsed && (
          <div
            className="absolute bottom-0 left-0 right-0 p-4 flex justify-center items-center pointer-events-none select-none"
            style={{ zIndex: 0 }}
          >
            <img
              src={sidebarLog}
              alt="Ayurvedic Brand Logo"
              className={`w-full max-h-[250px] object-contain transition-opacity duration-300 ${
                isScrolled ? "opacity-35" : "opacity-100"
              }`}
            />
          </div>
        )}
      </aside>
    </>
  );
}

export default Sidebar;
